'use client'

import { useEffect, useRef, useState } from 'react'

import { buildScreenAssistant } from './vapi-assistant'

const VAPI_KEY = process.env.NEXT_PUBLIC_VAPI_PUBLIC_KEY || null

// The interviewer's TTS pauses get transcribed as filler words ("Uh,", "Um,")
// even though the underlying text has none. Scrub them from the interviewer's
// captions and transcript — the candidate's own words stay verbatim.
function polishInterviewerText(text) {
  return text
    .replace(/(^|[.?!—-]\s*)(uh|um)[,.]?\s+/gi, '$1')
    .replace(/\s+(uh|um)[,.]\s+/gi, ' ')
    .replace(/\s{2,}/g, ' ')
    .replace(/(^|[.?!]\s+)([a-z])/g, (m, p, c) => p + c.toUpperCase())
    .trim()
}

/**
 * Live Vapi voice interview: real-time STT/TTS with natural turn-taking.
 * Finalized captions stream into `onTurn` (for the visible transcript) and
 * sync to /api/careers/interview/voice-sync so the application record —
 * and therefore scoring and the hiring team — see exactly what was said.
 * At call end the server runs the extraction pass and may auto-complete
 * the screen. Mirrors zero-to-hero/apply/useVapiInterview.js.
 */
export function useCareersVoice({
  token,
  candidateName,
  postingTitle,
  interviewFocus,
  salaryRange,
  onTurn,
  onComplete,
  onIncomplete,
  onError,
}) {
  const [callState, setCallState] = useState('idle') // idle|connecting|active|ending|finalizing
  const [assistantSpeaking, setAssistantSpeaking] = useState(false)
  const [liveCaption, setLiveCaption] = useState(null) // { role, text } partials
  const [muted, setMuted] = useState(false)

  const vapiRef = useRef(null)
  const queueRef = useRef([])
  const chainRef = useRef(Promise.resolve(null))
  const lastFinalRef = useRef({ candidate: '', interviewer: '' })
  const fatalSyncRef = useRef(false) // one permanent-sync-failure notice per call
  const liveRef = useRef(false) // the call actually connected this session
  const finalizedRef = useRef(false) // end-of-call sync ran (call-end OR error)
  const handlersRef = useRef({})
  handlersRef.current = { onTurn, onComplete, onIncomplete, onError }

  const available = Boolean(VAPI_KEY && token)

  async function doFlush(endCall) {
    const turns = queueRef.current.splice(0, queueRef.current.length)
    if (turns.length === 0 && !endCall) return null
    try {
      const res = await fetch('/api/careers/interview/voice-sync', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, turns, endCall }),
        // The end-of-call request triggers the extraction pass and races the
        // candidate closing the tab — keepalive lets it survive page unload.
        keepalive: endCall,
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) {
        const err = new Error(
          data.error ||
            "We couldn't save part of your interview — check your connection and try again.",
        )
        // 404/409/410 mean the application can't take turns (link dead,
        // already submitted) — retrying can never succeed.
        err.permanent =
          res.status === 404 || res.status === 409 || res.status === 410
        throw err
      }
      return data
    } catch (err) {
      if (err.permanent) {
        // Nothing said here can be saved — drop the queue, tell the candidate
        // once, and end the call instead of interviewing into the void.
        queueRef.current.length = 0
        if (!fatalSyncRef.current) {
          fatalSyncRef.current = true
          handlersRef.current.onError?.(err.message)
          try {
            vapiRef.current?.stop()
          } catch {
            /* already stopped */
          }
        }
        return null
      }
      // Put the turns back so nothing said is lost; the next flush retries.
      queueRef.current.unshift(...turns)
      if (endCall) handlersRef.current.onError?.(err.message)
      return null
    }
  }

  // Syncs are SERIALIZED: the server append is read-modify-write, so two
  // concurrent flushes would clobber each other's turns.
  function flush(endCall = false) {
    chainRef.current = chainRef.current
      .then(() => doFlush(endCall))
      .catch(() => null)
    return chainRef.current
  }

  // The end of every call funnels through here exactly once, whether the SDK
  // reports it as `call-end` OR as an `error` (the assistant hanging up after
  // its goodbye ejects the browser from the room, which Vapi surfaces as an
  // error event — that's the NORMAL exit for a finished interview). Runs the
  // end-of-call sync (extraction + possible auto-complete) and only then
  // decides whether anything actually went wrong.
  async function finalize({ dropped = false } = {}) {
    if (finalizedRef.current) return
    finalizedRef.current = true
    setCallState('finalizing')
    const result = await flush(true)
    setCallState('idle')
    if (result?.complete) {
      handlersRef.current.onComplete?.()
    } else if (result) {
      // Finalization saved everything but coverage isn't full — hand the
      // caller what's missing so the UI can point at the next question
      // instead of leaving the candidate guessing (or restarting).
      handlersRef.current.onIncomplete?.({
        remaining: result.remaining || [],
        dropped,
      })
    }
    // result === null → doFlush already surfaced the sync failure itself.
  }

  // `remaining`/`resuming` flow into the assistant so a returning candidate's
  // call greets them with what's left instead of restarting the interview.
  async function start({ remaining = [], resuming = false } = {}) {
    if (!available || callState !== 'idle') return
    fatalSyncRef.current = false
    liveRef.current = false
    finalizedRef.current = false
    setCallState('connecting')
    try {
      const { default: Vapi } = await import('@vapi-ai/web')
      const vapi = new Vapi(VAPI_KEY)
      vapiRef.current = vapi

      vapi.on('call-start', () => {
        liveRef.current = true
        setCallState('active')
        setMuted(false)
      })
      vapi.on('speech-start', () => setAssistantSpeaking(true))
      vapi.on('speech-end', () => setAssistantSpeaking(false))

      vapi.on('message', (m) => {
        if (m?.type !== 'transcript' || !m.transcript) return
        // Transcripts flowing = the call is live, even if call-start was missed.
        liveRef.current = true
        setCallState((s) => (s === 'connecting' ? 'active' : s))
        const role = m.role === 'assistant' ? 'interviewer' : 'candidate'
        const clean = (t) =>
          role === 'interviewer' ? polishInterviewerText(t) : t.trim()
        if (m.transcriptType === 'partial') {
          setLiveCaption({ role, text: clean(m.transcript) })
          return
        }
        // Final caption: show it, queue it, sync it. Vapi finalizes speech in
        // fragments — consecutive same-role fragments coalesce into one turn.
        setLiveCaption(null)
        const text = clean(m.transcript)
        if (!text || lastFinalRef.current[role] === text) return
        lastFinalRef.current[role] = text
        handlersRef.current.onTurn?.({ role, text })
        const lastQueued = queueRef.current[queueRef.current.length - 1]
        if (lastQueued && lastQueued.role === role) {
          lastQueued.text = `${lastQueued.text} ${text}`.slice(0, 2000)
        } else {
          queueRef.current.push({ role, text })
        }
        flush(false)
      })

      vapi.on('call-end', async () => {
        // Keep the modal up in a "wrapping up" state until the end-of-call
        // sync (which runs the extraction) resolves — candidates see their
        // application finalize instead of silently losing the last step.
        setAssistantSpeaking(false)
        setLiveCaption(null)
        setMuted(false)
        // No dropMessage: an incomplete interview here means the candidate
        // pressed stop deliberately — the chat is still open for text.
        await finalize()
      })

      vapi.on('error', async (e) => {
        console.error('Vapi error:', e)
        setAssistantSpeaking(false)
        setLiveCaption(null)
        setMuted(false)
        if (!liveRef.current) {
          // Never connected — nothing to save, just report the failure.
          setCallState('idle')
          handlersRef.current.onError?.(
            e?.errorMsg ||
              e?.message ||
              'Could not start the voice interview — you can continue by text.',
          )
          return
        }
        // The call WAS live, so everything said is synced — finalize exactly
        // like call-end. A completed interview submits with no error shown
        // (this is how normal assistant hangups arrive); a genuinely dropped
        // one saves its evidence, then onIncomplete points the candidate at
        // exactly what's left.
        await finalize({ dropped: true })
      })

      await vapi.start(
        buildScreenAssistant({
          candidateName,
          postingTitle,
          interviewFocus,
          salaryRange,
          remaining,
          resuming,
        }),
      )
    } catch (err) {
      setCallState('idle')
      handlersRef.current.onError?.(
        err?.message?.includes('Permission')
          ? 'Microphone access was blocked — allow it in your browser, or answer by text.'
          : err?.message ||
              'Could not start the voice interview — you can continue by text.',
      )
    }
  }

  function stop() {
    setCallState('ending')
    try {
      vapiRef.current?.stop()
    } catch {
      setCallState('idle')
    }
  }

  // Mic mute during the live call — the interviewer keeps talking, but hears
  // nothing until unmuted. The SDK's own state is the source of truth.
  function toggleMute() {
    const vapi = vapiRef.current
    if (!vapi) return
    try {
      const next = !vapi.isMuted()
      vapi.setMuted(next)
      setMuted(next)
    } catch {
      /* call not live yet — leave state as is */
    }
  }

  useEffect(
    () => () => {
      try {
        vapiRef.current?.stop()
      } catch {
        /* unmount cleanup */
      }
    },
    [],
  )

  return {
    available,
    callState,
    assistantSpeaking,
    liveCaption,
    muted,
    toggleMute,
    start,
    stop,
  }
}
